<template>
  <v-container fluid>
    <v-row dense>
      <v-col cols="12" sm="6">
        <Plotly :data="annualLoadChart.data" :layout="annualLoadChart.layout" />
      </v-col>
      <v-col cols="12" sm="6">
        <p class="text-caption text-center mt-2 mb-0">Peak demand based on the α and β formula</p>
        <Plotly :data="peakLoadChart.data" :layout="peakLoadChart.layout" />
      </v-col>
    </v-row>
    <v-row dense>
      <v-col cols="12" lg="4" style="max-width: 500px">
        <Widget v-if="outputs.metrics" title="Regression Metrics" height="100%">
          <v-simple-table>
            <template #default>
              <tbody>
                <tr>
                  <td>
                    Multiple R
                    <v-tooltip top>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon v-on="on" v-bind="attrs" small color="grey" class="ml-1"
                          >mdi-help-circle</v-icon
                        >
                      </template>
                      <span
                        >The correlation coefficient between observed and predicted values. Ranges
                        from 0 to 1, where 1 indicates perfect correlation.</span
                      >
                    </v-tooltip>
                  </td>
                  <td>{{ formatMetricValue(outputs.metrics.multiple_r) }}</td>
                </tr>
                <tr>
                  <td>
                    R<sup>2</sup>
                    <v-tooltip top>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon v-on="on" v-bind="attrs" small color="grey" class="ml-1"
                          >mdi-help-circle</v-icon
                        >
                      </template>
                      <span
                        >Coefficient of determination. Shows what percentage of variance in the data
                        is explained by the model. 0-1 range, higher is better. 0.7+ is generally
                        considered good.</span
                      >
                    </v-tooltip>
                  </td>
                  <td>{{ formatMetricValue(outputs.metrics.r_square) }}</td>
                </tr>
                <tr>
                  <td>
                    Adjusted R<sup>2</sup>
                    <v-tooltip top>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon v-on="on" v-bind="attrs" small color="grey" class="ml-1"
                          >mdi-help-circle</v-icon
                        >
                      </template>
                      <span
                        >R² adjusted for the number of predictors in the model. More reliable than
                        R² when comparing models with different numbers of variables.</span
                      >
                    </v-tooltip>
                  </td>
                  <td>{{ formatMetricValue(outputs.metrics.adjusted_r_square) }}</td>
                </tr>
                <tr>
                  <td>
                    Standard Error Regression (GWh)
                    <v-tooltip top>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon v-on="on" v-bind="attrs" small color="grey" class="ml-1"
                          >mdi-help-circle</v-icon
                        >
                      </template>
                      <span
                        >Average distance between observed and predicted values. Lower values
                        indicate better model accuracy. Units match your dependent variable.</span
                      >
                    </v-tooltip>
                  </td>
                  <td>{{ formatMetricValue(outputs.metrics.standard_error_regression) }}</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>

          <!-- Collapsible -->
          <v-expansion-panels class="mt-3">
            <v-expansion-panel>
              <v-expansion-panel-header>
                <span class="text-subtitle-2">More</span>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-simple-table>
                  <template #default>
                    <tbody>
                      <tr>
                        <td>Observations</td>
                        <td>{{ formatMetricValue(outputs.metrics.observations) }}</td>
                      </tr>
                      <tr>
                        <td>F Statistic</td>
                        <td>{{ formatMetricValue(outputs.metrics.f_statistic) }}</td>
                      </tr>
                      <tr>
                        <td>P value of F statistic</td>
                        <td>{{ formatMetricValue(outputs.metrics.p_value_f) }}</td>
                      </tr>
                      <tr>
                        <td>Degrees of Freedom (Total)</td>
                        <td>{{ formatMetricValue(outputs.metrics.df_total) }}</td>
                      </tr>
                      <tr>
                        <td>Degrees of Freedom (Regression)</td>
                        <td>{{ formatMetricValue(outputs.metrics.df_regression) }}</td>
                      </tr>
                      <tr>
                        <td>Degrees of Freedom (Residual)</td>
                        <td>{{ formatMetricValue(outputs.metrics.df_residual) }}</td>
                      </tr>
                      <tr>
                        <td>Sum of Squares (Total)</td>
                        <td>{{ formatMetricValue(outputs.metrics.ss_total) }}</td>
                      </tr>
                      <tr>
                        <td>Sum of Squares (Regression)</td>
                        <td>{{ formatMetricValue(outputs.metrics.ss_regression) }}</td>
                      </tr>
                      <tr>
                        <td>Sum of Squares (Residual)</td>
                        <td>{{ formatMetricValue(outputs.metrics.ss_residual) }}</td>
                      </tr>
                      <tr>
                        <td>Mean Square (Regression)</td>
                        <td>{{ formatMetricValue(outputs.metrics.ms_regression) }}</td>
                      </tr>
                      <tr>
                        <td>Mean Square (Residual)</td>
                        <td>{{ formatMetricValue(outputs.metrics.ms_residual) }}</td>
                      </tr>
                      <tr v-if="outputs.metrics_ridge && outputs.metrics_ridge.intercept">
                        <td>Intercept</td>
                        <td>{{ formatMetricValue(outputs.metrics_ridge.intercept) }}</td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </Widget>
      </v-col>
      <v-col cols="12" lg="8">
        <Widget title="Feature Specific Regression Metrics" height="100%">
          <v-simple-table>
            <template #default>
              <thead>
                <tr>
                  <th>Feature</th>
                  <th>
                    Coefficient
                    <v-tooltip top>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon v-on="on" v-bind="attrs" small color="grey" class="ml-1"
                          >mdi-help-circle</v-icon
                        >
                      </template>
                      <span
                        >The effect of each feature on the load. Positive values increase load,
                        negative values decrease it. Larger absolute values indicate stronger
                        influence.</span
                      >
                    </v-tooltip>
                  </th>
                  <th>
                    Std Error
                    <v-tooltip top>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon v-on="on" v-bind="attrs" small color="grey" class="ml-1"
                          >mdi-help-circle</v-icon
                        >
                      </template>
                      <span
                        >Uncertainty in the coefficient estimate. Smaller values indicate more
                        precise estimates.</span
                      >
                    </v-tooltip>
                  </th>
                  <th>
                    t-Stat
                    <v-tooltip top>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon v-on="on" v-bind="attrs" small color="grey" class="ml-1"
                          >mdi-help-circle</v-icon
                        >
                      </template>
                      <span
                        >Test statistic for coefficient significance. Values &gt; 2 or &lt; -2
                        generally indicate statistical significance.</span
                      >
                    </v-tooltip>
                  </th>
                  <th>
                    P-value
                    <v-tooltip top>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon v-on="on" v-bind="attrs" small color="grey" class="ml-1"
                          >mdi-help-circle</v-icon
                        >
                      </template>
                      <span
                        >Probability that the coefficient is actually zero. Values &lt; 0.05
                        indicate statistical significance at 95% confidence.</span
                      >
                    </v-tooltip>
                  </th>
                  <th>
                    Lower 95% CI
                    <v-tooltip top>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon v-on="on" v-bind="attrs" small color="grey" class="ml-1"
                          >mdi-help-circle</v-icon
                        >
                      </template>
                      <span>Lower bound of the 95% confidence interval for the coefficient.</span>
                    </v-tooltip>
                  </th>
                  <th>
                    Upper 95% CI
                    <v-tooltip top>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon v-on="on" v-bind="attrs" small color="grey" class="ml-1"
                          >mdi-help-circle</v-icon
                        >
                      </template>
                      <span>Upper bound of the 95% confidence interval for the coefficient.</span>
                    </v-tooltip>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="feature in featureNames" :key="feature">
                  <td class="font-weight-medium">{{ formatFeatureName(feature) }}</td>
                  <td>{{ formatMetricValue(outputs.metrics_ridge.coefficients[feature]) }}</td>
                  <td>{{ formatMetricValue(outputs.metrics_ridge.standard_errors[feature]) }}</td>
                  <td>{{ formatMetricValue(outputs.metrics_ridge.t_stats[feature]) }}</td>
                  <td>
                    {{ formatPValue(outputs.metrics_ridge.p_values_coefficients[feature]) }}
                  </td>
                  <td>
                    {{ formatMetricValue(outputs.metrics_ridge.confidence_intervals[feature][0]) }}
                  </td>
                  <td>
                    {{ formatMetricValue(outputs.metrics_ridge.confidence_intervals[feature][1]) }}
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </Widget>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { DateTime } from "luxon";

function commonLayout(chartTitle, yAxisTitle) {
  return {
    title: chartTitle,
    height: "400",
    margin: { pad: 0, t: 48, b: 64, l: 40, r: 40 },
    font: { size: 10 },
    xaxis: {
      type: "date",
      tickformat: "%Y",
      title: {
        text: "Years",
      },
    },
    yaxis: {
      rangemode: "tozero",
      title: {
        text: yAxisTitle,
      },
    },
    showlegend: true,
    legend: {
      orientation: "h",
    },
  };
}

export default {
  name: "LoadForecastDashboard",
  components: {
    Plotly: () => import("@/components/charts/PlotlyWrapper.vue"),
    Widget: () => import("@/components/visualizations/Widget.vue"),
  },
  mixins: [],
  props: {
    outputs: { type: Object, required: true },
    inputCase: { type: Object, required: true },
  },
  data() {
    return {};
  },
  computed: {
    featureNames() {
      // Get feature names from the ridge metrics keys
      return Object.keys(this.outputs.metrics_ridge.coefficients || {});
    },
    historicYears() {
      const startYear = this.inputCase.settings.year_start;
      const presentYear = this.inputCase.settings.LOAD_FORECAST.year_present;
      const dataPoints = [];
      // present year excluded, outputs are always yearly
      for (let year = startYear; year < presentYear; year += 1) {
        dataPoints.push(DateTime.fromObject({ year }).toISODate());
      }
      return dataPoints;
    },
    forecastYears() {
      const presentYear = this.inputCase.settings.LOAD_FORECAST.year_present;
      const endYear = this.inputCase.settings.year_end;
      const dataPoints = [];
      // present year included, outputs are always yearly
      for (let year = presentYear; year <= endYear; year += 1) {
        dataPoints.push(DateTime.fromObject({ year }).toISODate());
      }
      return dataPoints;
    },
    peakLoadChart() {
      const chartTitle = "Annual Peak Demand Forecast";
      const layout = commonLayout(chartTitle, "Peak Load (MW)");
      return {
        data: [
          {
            x: this.historicYears,
            y: this.roundArray(this.outputs.historicalFormulaPeakLoads),
            name: "Historic",
            type: "scatter",
          },
          {
            x: this.forecastYears,
            y: this.roundArray(this.outputs.peakLoads),
            name: "Forecast",
            type: "scatter",
            line: {
              dash: "dash",
            },
          },
        ],
        layout,
      };
    },
    annualLoadChart() {
      const chartTitle = "Annual Energy Demand Forecast";
      const layout = commonLayout(chartTitle, "Annual Load (GWh)");
      return {
        data: [
          {
            x: this.historicYears,
            y: this.roundArray(this.outputs.historicalAnnualLoads),
            name: "Historic",
            type: "scatter",
          },
          {
            x: this.forecastYears,
            y: this.roundArray(this.outputs.annualLoads),
            name: "Forecast",
            type: "scatter",
            line: {
              dash: "dash",
            },
          },
        ],
        layout,
      };
    },
  },
  watch: {},
  beforeCreate() {},
  created() {},
  beforeMount() {},
  mounted() {},
  beforeUpdate() {},
  updated() {},
  activated() {},
  deactivated() {},
  beforeDestroy() {},
  destroyed() {},
  methods: {
    formatMetricValue(value) {
      if (value === null || value === undefined || Number.isNaN(value)) {
        return "N/A";
      }
      if (Number.isInteger(value)) {
        return value;
      }
      return value.toFixed(4);
    },
    formatPValue(value) {
      if (value === null || value === undefined || Number.isNaN(value)) {
        return "N/A";
      }
      if (value < 0.001) {
        return "< 0.001";
      }
      return value.toFixed(3);
    },
    formatFeatureName(feature) {
      const nameMap = {
        gdp: "GDP",
        population: "Population",
        evs: "Electric Vehicles",
        energy_intensity: "Energy Intensity",
      };
      return nameMap[feature] || feature;
    },
    roundArray(arr) {
      return arr.map((val) => Math.round(val));
    },
  },
};
</script>

<style></style>
